import { TSelectValue } from '@models/common';
import {
  TAssetListReq,
  TAssetListState,
  TInventorySessionItem,
} from '@models/asset';
import { assetApi } from '@services/assetApi';
import { inventorySessionApi } from '@services/inventorySessionApi';
import { layoutApi } from '@services/layoutApi';
import { guidGenerator, handleShowToastError } from '@utils/helpers';
import { FormInstance } from 'antd/lib';
import { SortOrder } from 'antd/es/table/interface';
import { ALL_DEVICE_CONSTANT } from '../constant';
import { COMMON_MESSAGE_ERROR, ZENBU_OPTION } from '@constants/common';
import { NavigateFunction } from 'react-router-dom';
import { TAxiosResponse } from 'types';
import { EHttpStatusCode } from 'types/service';
import { appToast } from '@utils/appToast';
import _ from 'lodash';

const { NAME, MESSAGE: DEVICE_MSG } = ALL_DEVICE_CONSTANT;

// GET /api/v1/assets — hỗ trợ sort đủ 8 cột theo vams_docs/Tasks/PC004/analyzing-be.md §2.2 (G-SORT).
// Key = dataIndex cột (lowercase) trong columns.tsx, value = tên field BE nhận qua SortBy.
const SORT_FIELD_TO_BACKEND: Record<string, string> = {
  assetcode: 'assetNo',
  purchasedate: 'acquisitionDate',
  purchaseprice: 'acquisitionCost',
  assettype: 'assetType',
  departmentname: 'departmentName',
  location: 'location',
  usefullife: 'usefulLife',
  bookvaluestart: 'bookValueStart',
  latestupdatedat: 'latestUpdatedAt',
  hasphoto: 'hasPhoto',
};

// giữ 7 năm gần nhất (spec [3] 年月) — kỳ hiện tại + 6 năm về trước.
const MAX_INVENTORY_YEARS = 7;

function useAllDeviceApi() {
  const handleNavigateBack = (navigate: NavigateFunction) => {
    if (window.history?.length && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleSortOrder = (direction: string) => {
    switch (direction) {
      case 'ascend':
        return 'asc';
      case 'descend':
        return 'desc';
      default:
        return 'asc';
    }
  };

  const handleSortOrderV2 = (order: string): SortOrder => {
    switch (order) {
      case 'asc':
        return 'ascend';
      case 'desc':
        return 'descend';
      default:
        return 'ascend';
    }
  };

  const mapAssetResponse = (
    response: TAxiosResponse<any, any>
  ): TAssetListState => {
    const items = (response.data || []).map((item: any) => ({
      ...item,
      key: item.assetId || guidGenerator(),
      // inventoryStatusName đã là nhãn JP đủ 6 status tính theo kỳ (xem analyzing-be.md §2.2).
      status: item.inventoryStatusName || undefined,
    }));

    return {
      totalCount: response.pagination?.totalCount ?? items.length,
      currentPage: response.pagination?.currentPage ?? 1,
      pageSize: response.pagination?.pageSize ?? items.length,
      hasNext: response.pagination?.hasNext ?? false,
      data: items,
      companyTotalCount:
        response.pagination?.companyTotalCount ??
        response.pagination?.totalCount ??
        items.length,
    };
  };

  const buildSortParams = (sortField: string, sortDirection: string) => {
    const backendField = SORT_FIELD_TO_BACKEND[sortField?.toLowerCase()];
    if (!backendField) {
      return {};
    }
    return { sortBy: backendField, ascending: sortDirection !== 'desc' };
  };

  // EFFECT
  // 会社 [4.1] spec: "Default: blank (all)" cho 管理者 — chỉ pre-fill khi có ?companyCode=
  // trên URL (deep-link từ màn 会社マスタ一覧); không tự chọn company đầu tiên trong list.
  const effectCompanies = (
    form: FormInstance,
    searchParams: URLSearchParams
  ) => {
    const companyCode = searchParams.get('companyCode');
    form.setFieldValue(NAME.COMPANY, companyCode || '');
  };

  // API
  const getCompanies = async (
    companyCode: string,
    setCompaniesSelect: (val: TSelectValue[]) => void,
    setLoadingPage: (val: boolean) => void,
    onCompaniesLoaded?: (companyCode: string, companyName: string) => void
  ) => {
    try {
      const response = await layoutApi.getCompanies();

      if (response.ok) {
        const convertCompanies = response.data.map((val) => ({
          value: val.companyCode,
          label: `${val.companyCode}_${val.companyName}`,
          children: val.id,
        }));
        const findCompany = _.find(response.data, function (item) {
          return item.companyCode === companyCode;
        });
        setCompaniesSelect([ZENBU_OPTION, ...convertCompanies]);

        const resolvedCompanyCode = findCompany
          ? findCompany.companyCode
          : response.data[0]?.companyCode;
        const resolvedCompanyName = findCompany
          ? findCompany.companyName
          : response.data[0]?.companyName;
        if (resolvedCompanyCode) {
          onCompaniesLoaded?.(resolvedCompanyCode, resolvedCompanyName);
        }
      }
    } catch (error) {
      handleShowToastError(error);
    } finally {
      setLoadingPage(false);
    }
  };

  // [4.2] 店舗 dropdown (chỉ role không phải 各店舗 mới gọi — role 各店舗 khoá field, dùng store của
  // chính user, xem useAllDevice.tsx).
  const getStoresByCompany = async (
    companyCode: string,
    companyName: string,
    setStoresSelect: (val: TSelectValue[]) => void,
    setLoadingOther: (val: boolean) => void
  ) => {
    setLoadingOther(true);
    try {
      const response = await layoutApi.getStoresByCompany({
        companyCode,
        companyName,
      });

      if (response.ok) {
        setStoresSelect([
          ZENBU_OPTION,
          ...response.data.map((val) => ({
            value: val.storeCode,
            label: `${val.storeCode}_${val.storeName}`,
            children: val.id,
          })),
        ]);
      }
    } catch (error) {
      handleShowToastError(error);
    } finally {
      setLoadingOther(false);
    }
  };

  // [4.1]/[4.3]/[4.4]/[4.6] 会社・種類・部門・所属 dropdown — GET /api/v1/assets/lookups.
  // 会社 [4.1] và 部門 [4.4] options đều lấy từ cột thô (CompanyCode/DepartmentCode) trên t_assets,
  // độc lập hoàn toàn với 店舗 (không còn phụ thuộc quan hệ m_stores.CompanyId).
  const getLookups = async (
    setDepartmentsSelect: (val: TSelectValue[]) => void,
    setLocationsSelect: (val: TSelectValue[]) => void,
    setAssetTypesSelect: (val: TSelectValue[]) => void,
    setCompaniesSelect: (val: TSelectValue[]) => void,
    setLoadingOther: (val: boolean) => void,
    setLoadingPage?: (val: boolean) => void
  ) => {
    setLoadingOther(true);
    try {
      const response = await assetApi.getAssetLookups();

      if (response.ok) {
        setCompaniesSelect([
          ZENBU_OPTION,
          ...response.data.companies.map((val) => ({
            value: val.companyCode,
            label: `${val.companyCode}_${val.companyName}`,
          })),
        ]);
        setDepartmentsSelect([
          ZENBU_OPTION,
          ...response.data.departments.map((val) => ({
            value: val.departmentCode,
            label: val.departmentName || val.departmentCode,
          })),
        ]);
        setLocationsSelect([
          ZENBU_OPTION,
          ...response.data.locations.map((val) => ({ value: val, label: val })),
        ]);
        setAssetTypesSelect([
          ZENBU_OPTION,
          ...response.data.assetTypes.map((val) => ({
            value: val,
            label: val,
          })),
        ]);
      }
    } catch (error) {
      handleShowToastError(error);
    } finally {
      setLoadingOther(false);
      setLoadingPage?.(false);
    }
  };

  // [3] 年月 dropdown (Năm/Tháng tách riêng qua SessionYearMonthSelect) — tái dùng
  // GET /api/v1/inventory/sessions, dedupe theo inventoryYearMonth, giới hạn 7 năm gần nhất,
  // sort desc (xem analyzing-be.md §2.2/§2.4). Trả nguyên sessions, mapping Năm/Tháng do component lo.
  const getInventorySessions = async (
    setSessionsSelect: (val: TInventorySessionItem[]) => void,
    onSettled: () => void
  ) => {
    try {
      const response = await inventorySessionApi.getSessions();

      if (response.ok) {
        const currentYear = new Date().getFullYear();
        const minYear = currentYear - (MAX_INVENTORY_YEARS - 1);

        const sessions = _.chain(response.data)
          .filter(
            (item) => !item.inventoryYear || item.inventoryYear >= minYear
          )
          .uniqBy((item) => item.inventoryYearMonth)
          .orderBy((item) => item.inventoryYearMonth, 'desc')
          .value();

        setSessionsSelect(sessions);
      }
    } catch (error) {
      handleShowToastError(error);
    } finally {
      onSettled();
    }
  };

  const getAssetsSearch = async (
    payload: TAssetListReq,
    setLoadingPage: (val: boolean) => void,
    setResultData: (val: TAssetListState) => void,
    onServerError?: (msg: string) => void
  ) => {
    setLoadingPage(true);

    try {
      const response = await assetApi.getAssets(payload);

      if (response.ok) {
        setResultData(mapAssetResponse(response));
      }
    } catch (error) {
      setResultData({
        data: [],
        totalCount: 0,
        currentPage: 1,
        pageSize: 0,
        hasNext: false,
        companyTotalCount: 0,
      });

      const err = error as TAxiosResponse;
      if (err.status === EHttpStatusCode.FORBIDDEN) {
        appToast.error({ message: COMMON_MESSAGE_ERROR });
        return;
      }

      if (err.status === EHttpStatusCode.INTERNAL_SERVER_ERROR) {
        onServerError?.(DEVICE_MSG.LOAD_ERROR);
        return;
      }

      handleShowToastError(error);
    } finally {
      setLoadingPage(false);
    }
  };

  const getAssetsScroll = async (
    payload: TAssetListReq,
    resultData: TAssetListState,
    setLoadingPage: (val: boolean) => void,
    setResultData: (val: TAssetListState) => void
  ) => {
    setLoadingPage(true);

    try {
      const response = await assetApi.getAssets(payload);

      if (response.ok) {
        const next = mapAssetResponse(response);
        if (resultData.data.length !== next.totalCount) {
          setResultData({
            ...next,
            data: [...resultData.data, ...next.data],
          });
        }
      }
    } catch (error) {
      handleShowToastError(error);
    } finally {
      setLoadingPage(false);
    }
  };

  const getAssetsSort = async (
    payload: TAssetListReq,
    resultData: TAssetListState,
    setLoadingPage: (val: boolean) => void,
    setResultData: (val: TAssetListState) => void
  ) => {
    setLoadingPage(true);

    try {
      const response = await assetApi.getAssets(payload);

      if (response.ok) {
        setResultData(mapAssetResponse(response));
      }
    } catch (error) {
      handleShowToastError(error);
      setResultData({
        data: [],
        totalCount: 0,
        currentPage: 1,
        pageSize: 0,
        hasNext: false,
        companyTotalCount: 0,
      });
    } finally {
      setLoadingPage(false);
    }
  };

  // [7] エクスポート — GET /api/v1/assets/export theo cùng filter hiện tại (không phân trang,
  // không cần confirm dialog theo spec). Xem analyzing-be.md §2.4 (G-EXPORT).
  const exportAssets = async (
    payload: TAssetListReq,
    setLoadingExport: (val: boolean) => void
  ) => {
    setLoadingExport(true);
    try {
      const blob = (await assetApi.exportAssets(payload)) as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const elementAnchor = document.createElement('a');
      elementAnchor.href = url;
      elementAnchor.download = `asset_ledger_${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, '')}.csv`;
      elementAnchor.click();
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      handleShowToastError(error);
      return false;
    } finally {
      setLoadingExport(false);
    }
  };

  // [9.20] 削除 — DELETE /api/v1/assets/{assetId}, chỉ áp dụng row 不記載 (soft delete ở BE).
  // Xem analyzing-be.md §2.4 (G-DELETE).
  const deleteAsset = async (
    assetId: string,
    setLoadingPage: (val: boolean) => void
  ) => {
    setLoadingPage(true);
    try {
      const response = await assetApi.deleteAsset(assetId);
      return !!response.ok;
    } catch (error) {
      return false;
    } finally {
      setLoadingPage(false);
    }
  };

  return {
    handler: {
      handleNavigateBack,
      handleSortOrderV2,
      handleSortOrder,
      buildSortParams,

      getCompanies,
      getStoresByCompany,
      getLookups,
      getInventorySessions,

      getAssetsSearch,
      getAssetsScroll,
      getAssetsSort,

      exportAssets,
      deleteAsset,
    },
    effect: {
      effectCompanies,
    },
  };
}

export default useAllDeviceApi;
